import _createClass from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/createClass";
import _classCallCheck from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck";

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { CursorColumns, SingleCursorState } from './cursorCommon.js';
import { Position } from '../core/position.js';
import { Range } from '../core/range.js';
import * as strings from '../../../base/common/strings.js';
export var CursorPosition = function CursorPosition(lineNumber, column, leftoverVisibleColumns) {
  _classCallCheck(this, CursorPosition);

  this.lineNumber = lineNumber;
  this.column = column;
  this.leftoverVisibleColumns = leftoverVisibleColumns;
};
export var MoveOperations = /*#__PURE__*/function () {
  function MoveOperations() {
    _classCallCheck(this, MoveOperations);
  }

  _createClass(MoveOperations, null, [{
    key: "leftPosition",
    value: function leftPosition(model, lineNumber, column) {
      if (column > model.getLineMinColumn(lineNumber)) {
        column = column - strings.prevCharLength(model.getLineContent(lineNumber), column - 1);
      } else if (lineNumber > 1) {
        lineNumber = lineNumber - 1;
        column = model.getLineMaxColumn(lineNumber);
      }

      return new Position(lineNumber, column);
    }
  }, {
    key: "left",
    value: function left(config, model, lineNumber, column) {
      var pos = MoveOperations.leftPosition(model, lineNumber, column);
      return new CursorPosition(pos.lineNumber, pos.column, 0);
    }
  }, {
    key: "moveLeft",
    value: function moveLeft(config, model, cursor, inSelectionMode, noOfColumns) {
      var lineNumber, column;

      if (cursor.hasSelection() && !inSelectionMode) {
        // If we are in selection mode, move left without selection cancels selection and puts cursor at the beginning of the selection
        lineNumber = cursor.selection.startLineNumber;
        column = cursor.selection.startColumn;
      } else {
        var r = MoveOperations.left(config, model, cursor.position.lineNumber, cursor.position.column - (noOfColumns - 1));
        lineNumber = r.lineNumber;
        column = r.column;
      }

      return cursor.move(inSelectionMode, lineNumber, column, 0);
    }
  }, {
    key: "rightPosition",
    value: function rightPosition(model, lineNumber, column) {
      if (column < model.getLineMaxColumn(lineNumber)) {
        column = column + strings.nextCharLength(model.getLineContent(lineNumber), column - 1);
      } else if (lineNumber < model.getLineCount()) {
        lineNumber = lineNumber + 1;
        column = model.getLineMinColumn(lineNumber);
      }

      return new Position(lineNumber, column);
    }
  }, {
    key: "right",
    value: function right(config, model, lineNumber, column) {
      var pos = MoveOperations.rightPosition(model, lineNumber, column);
      return new CursorPosition(pos.lineNumber, pos.column, 0);
    }
  }, {
    key: "moveRight",
    value: function moveRight(config, model, cursor, inSelectionMode, noOfColumns) {
      var lineNumber, column;

      if (cursor.hasSelection() && !inSelectionMode) {
        // If we are in selection mode, move right without selection cancels selection and puts cursor at the end of the selection
        lineNumber = cursor.selection.endLineNumber;
        column = cursor.selection.endColumn;
      } else {
        var r = MoveOperations.right(config, model, cursor.position.lineNumber, cursor.position.column + (noOfColumns - 1));
        lineNumber = r.lineNumber;
        column = r.column;
      }

      return cursor.move(inSelectionMode, lineNumber, column, 0);
    }
  }, {
    key: "down",
    value: function down(config, model, lineNumber, column, leftoverVisibleColumns, count, allowMoveOnLastLine) {
      var currentVisibleColumn = CursorColumns.visibleColumnFromColumn(model.getLineContent(lineNumber), column, config.tabSize) + leftoverVisibleColumns;
      var lineCount = model.getLineCount();
      var wasOnLastPosition = lineNumber === lineCount && column === model.getLineMaxColumn(lineNumber);
      lineNumber = lineNumber + count;

      if (lineNumber > lineCount) {
        lineNumber = lineCount;

        if (allowMoveOnLastLine) {
          column = model.getLineMaxColumn(lineNumber);
        } else {
          column = Math.min(model.getLineMaxColumn(lineNumber), column);
        }
      } else {
        column = CursorColumns.columnFromVisibleColumn2(config, model, lineNumber, currentVisibleColumn);
      }

      if (wasOnLastPosition) {
        leftoverVisibleColumns = 0;
      } else {
        leftoverVisibleColumns = currentVisibleColumn - CursorColumns.visibleColumnFromColumn(model.getLineContent(lineNumber), column, config.tabSize);
      }

      return new CursorPosition(lineNumber, column, leftoverVisibleColumns);
    }
  }, {
    key: "moveDown",
    value: function moveDown(config, model, cursor, inSelectionMode, linesCount) {
      var lineNumber, column;

      if (cursor.hasSelection() && !inSelectionMode) {
        // If we are in selection mode, move down acts relative to the end of selection
        lineNumber = cursor.selection.endLineNumber;
        column = cursor.selection.endColumn;
      } else {
        lineNumber = cursor.position.lineNumber;
        column = cursor.position.column;
      }

      var r = MoveOperations.down(config, model, lineNumber, column, cursor.leftoverVisibleColumns, linesCount, true);
      return cursor.move(inSelectionMode, r.lineNumber, r.column, r.leftoverVisibleColumns);
    }
  }, {
    key: "translateDown",
    value: function translateDown(config, model, cursor) {
      var selection = cursor.selection;
      var selectionStart = MoveOperations.down(config, model, selection.selectionStartLineNumber, selection.selectionStartColumn, cursor.selectionStartLeftoverVisibleColumns, 1, false);
      var position = MoveOperations.down(config, model, selection.positionLineNumber, selection.positionColumn, cursor.leftoverVisibleColumns, 1, false);
      return new SingleCursorState(new Range(selectionStart.lineNumber, selectionStart.column, selectionStart.lineNumber, selectionStart.column), selectionStart.leftoverVisibleColumns, new Position(position.lineNumber, position.column), position.leftoverVisibleColumns);
    }
  }, {
    key: "up",
    value: function up(config, model, lineNumber, column, leftoverVisibleColumns, count, allowMoveOnFirstLine) {
      var currentVisibleColumn = CursorColumns.visibleColumnFromColumn(model.getLineContent(lineNumber), column, config.tabSize) + leftoverVisibleColumns;
      var wasOnFirstPosition = lineNumber === 1 && column === 1;
      lineNumber = lineNumber - count;

      if (lineNumber < 1) {
        lineNumber = 1;

        if (allowMoveOnFirstLine) {
          column = model.getLineMinColumn(lineNumber);
        } else {
          column = Math.min(model.getLineMaxColumn(lineNumber), column);
        }
      } else {
        column = CursorColumns.columnFromVisibleColumn2(config, model, lineNumber, currentVisibleColumn);
      }

      if (wasOnFirstPosition) {
        leftoverVisibleColumns = 0;
      } else {
        leftoverVisibleColumns = currentVisibleColumn - CursorColumns.visibleColumnFromColumn(model.getLineContent(lineNumber), column, config.tabSize);
      }

      return new CursorPosition(lineNumber, column, leftoverVisibleColumns);
    }
  }, {
    key: "moveUp",
    value: function moveUp(config, model, cursor, inSelectionMode, linesCount) {
      var lineNumber, column;

      if (cursor.hasSelection() && !inSelectionMode) {
        // If we are in selection mode, move up acts relative to the beginning of selection
        lineNumber = cursor.selection.startLineNumber;
        column = cursor.selection.startColumn;
      } else {
        lineNumber = cursor.position.lineNumber;
        column = cursor.position.column;
      }

      var r = MoveOperations.up(config, model, lineNumber, column, cursor.leftoverVisibleColumns, linesCount, true);
      return cursor.move(inSelectionMode, r.lineNumber, r.column, r.leftoverVisibleColumns);
    }
  }, {
    key: "translateUp",
    value: function translateUp(config, model, cursor) {
      var selection = cursor.selection;
      var selectionStart = MoveOperations.up(config, model, selection.selectionStartLineNumber, selection.selectionStartColumn, cursor.selectionStartLeftoverVisibleColumns, 1, false);
      var position = MoveOperations.up(config, model, selection.positionLineNumber, selection.positionColumn, cursor.leftoverVisibleColumns, 1, false);
      return new SingleCursorState(new Range(selectionStart.lineNumber, selectionStart.column, selectionStart.lineNumber, selectionStart.column), selectionStart.leftoverVisibleColumns, new Position(position.lineNumber, position.column), position.leftoverVisibleColumns);
    }
  }, {
    key: "moveToBeginningOfLine",
    value: function moveToBeginningOfLine(config, model, cursor, inSelectionMode) {
      var lineNumber = cursor.position.lineNumber;
      var minColumn = model.getLineMinColumn(lineNumber);
      var firstNonBlankColumn = model.getLineFirstNonWhitespaceColumn(lineNumber) || minColumn;
      var column;
      var relevantColumnNumber = cursor.position.column;

      if (relevantColumnNumber === firstNonBlankColumn) {
        column = minColumn;
      } else {
        column = firstNonBlankColumn;
      }

      return cursor.move(inSelectionMode, lineNumber, column, 0);
    }
  }, {
    key: "moveToEndOfLine",
    value: function moveToEndOfLine(config, model, cursor, inSelectionMode, sticky) {
      var lineNumber = cursor.position.lineNumber;
      var maxColumn = model.getLineMaxColumn(lineNumber);
      return cursor.move(inSelectionMode, lineNumber, maxColumn, sticky ? 1073741824
      /* MAX_SAFE_SMALL_INTEGER */
      - maxColumn : 0);
    }
  }, {
    key: "moveToBeginningOfBuffer",
    value: function moveToBeginningOfBuffer(config, model, cursor, inSelectionMode) {
      return cursor.move(inSelectionMode, 1, 1, 0);
    }
  }, {
    key: "moveToEndOfBuffer",
    value: function moveToEndOfBuffer(config, model, cursor, inSelectionMode) {
      var lastLineNumber = model.getLineCount();
      var lastColumn = model.getLineMaxColumn(lastLineNumber);
      return cursor.move(inSelectionMode, lastLineNumber, lastColumn, 0);
    }
  }]);

  return MoveOperations;
}();